import type { ProjectDetail } from "@esi/shared";
import { ArrowUp, SquarePen } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { handOffQuestion } from "../home/handoff.ts";
import { navigate, projectConversationPath } from "../router.tsx";

/**
 * "Ask about this project" (spec `project-home` "Ask about this project"): a one-line field in
 * the project header, on every tab, where the viewer may start a conversation. Enter (or the
 * arrow) hands the question to `/projects/:id/c/new` exactly as the app Home does, where the
 * conversation is created and opens with the default effort; files are attached there. An empty
 * field just opens a new conversation. Hidden below `sm`, where the header keeps its pen control.
 */
export function HeaderAsk({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const id = useId();
  const [text, setText] = useState("");
  const href = projectConversationPath(project.id, "new");
  const ask = () => {
    const question = text.trim();
    navigate(href, question ? { state: handOffQuestion(question) } : undefined);
  };
  return (
    <form
      data-esi-content="composer"
      aria-label={t("projectOverview.ask")}
      data-header-ask
      className="group/ask relative ml-auto flex h-8 w-full max-w-96 min-w-44 items-center rounded-full border border-line bg-surface-raised pr-1 pl-3 transition-colors duration-(--duration-fast) focus-within:border-line-strong hover:border-line-strong max-sm:hidden"
      onSubmit={(event) => {
        event.preventDefault();
        ask();
      }}
    >
      <SquarePen
        aria-hidden="true"
        className="size-3.5 shrink-0 text-fg-muted group-focus-within/ask:text-fg-secondary"
        strokeWidth={1.75}
      />
      <label htmlFor={id} className="sr-only">
        {t("projectOverview.ask")}
      </label>
      <input
        id={id}
        value={text}
        onChange={(event) => setText(event.currentTarget.value)}
        placeholder={t("projectHome.askShort")}
        autoComplete="off"
        className="h-full min-w-0 flex-1 bg-transparent px-2 text-[13px] text-fg outline-none placeholder:text-fg-muted"
      />
      <button
        type="submit"
        aria-label={t("projectHome.askSend")}
        className={cn(
          "inline-flex size-6 shrink-0 items-center justify-center rounded-full transition-colors duration-(--duration-fast) focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
          text.trim()
            ? "bg-accent-solid text-accent-on hover:bg-accent-solid-hover"
            : "text-fg-muted hover:bg-surface-hover hover:text-fg",
        )}
      >
        <ArrowUp aria-hidden="true" className="size-3.5" strokeWidth={2} />
      </button>
    </form>
  );
}
